import React, { useEffect, useState, useRef } from "react";
import MDBox from "components/MDBox";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import SpeedIcon from "@mui/icons-material/Speed";

/**
 * Helper function to draw perfect, smooth SVG arcs 
 * Used here for the specific redline track on the gauge
 */
const describeArc = (x, y, radius, startAngle, endAngle) => {
  const polarToCartesian = (centerX, centerY, radius, angleInDegrees) => {
    const angleInRadians = (angleInDegrees - 90) * (Math.PI / 180.0);
    return {
      x: centerX + radius * Math.cos(angleInRadians),
      y: centerY + radius * Math.sin(angleInRadians)
    };
  };

  const start = polarToCartesian(x, y, radius, endAngle);
  const end = polarToCartesian(x, y, radius, startAngle);
  const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";

  return [
    "M", start.x, start.y,
    "A", radius, radius, 0, largeArcFlag, 0, end.x, end.y
  ].join(" ");
};

/**
 * RealisticSpeedometer: Photorealistic redesign matching the reference image.
 */
const RealisticSpeedometer = ({ value = 0, max = 200 }) => {
  const [displayValue, setDisplayValue] = useState(0);
  const requestRef = useRef();

  const animateSpeed = () => {
    setDisplayValue((prev) => {
      const target = Math.min(value, max);
      const diff = target - prev;

      // dynamic easing
      const easingFactor = 0.02; 
      const step = diff * easingFactor;

      // snap when very close
      if (Math.abs(diff) < 0.1) {
        return target;
      }

      return prev + step;
    });

    requestRef.current = requestAnimationFrame(animateSpeed);
  };

  useEffect(() => {
    requestRef.current = requestAnimationFrame(animateSpeed);
    return () => cancelAnimationFrame(requestRef.current);
  }, [value, max]);

  const center = 100;

  // Calculate Needle Angle: 0 is at -135deg, Max is at +135deg. (0deg is straight up)
  const angle = (displayValue - (max / 2)) * (135 / (max / 2));

  // Tick spacing scales with max so smaller-range gauges (e.g. 50 knots) still read cleanly
  const tickStep = max <= 50 ? 5 : max <= 100 ? 10 : 20;
  const majorEvery = tickStep * 2;

  // Generate Ticks and Number Labels
  const ticks = [];
  for (let i = 0; i <= max; i += tickStep) {
    const isMajor = i % majorEvery === 0;
    const tickAngleDeg = (i - (max / 2)) * (135 / (max / 2));
    const rad = (tickAngleDeg - 90) * (Math.PI / 180);

    const outerR = 92;
    const innerR = isMajor ? 80 : 86;

    const x1 = center + outerR * Math.cos(rad);
    const y1 = center + outerR * Math.sin(rad);
    const x2 = center + innerR * Math.cos(rad);
    const y2 = center + innerR * Math.sin(rad);

    ticks.push(
      <line
        key={`tick-${i}`}
        x1={x1}
        y1={y1}
        x2={x2}
        y2={y2}
        stroke="#ffffff"
        strokeWidth={isMajor ? 2.5 : 1.5}
        strokeLinecap="round"
      />
    );

    // Add Text only on major intervals
    if (isMajor) {
      const textR = 64; 
      const tx = center + textR * Math.cos(rad);
      const ty = center + textR * Math.sin(rad);
      ticks.push(
        <text
          key={`label-${i}`}
          x={tx}
          y={ty}
          fill="#ffffff"
          fontSize="11"
          fontWeight="bold"
          fontFamily="Arial, Helvetica, sans-serif"
          textAnchor="middle"
          dominantBaseline="central"
        >
          {i}
        </text>
      );
    }
  }

  // Redline zone covers the top 20% of the gauge, whatever max is
  const redArcStartAngle = ((max * 0.8) - (max / 2)) * (135 / (max / 2));
  const redArcEndAngle = (max - (max / 2)) * (135 / (max / 2));
  const redArcPath = describeArc(center, center, 48, redArcStartAngle, redArcEndAngle);

  return (
    // Height set to 220 to ensure the full circle is visible
    <MDBox sx={{ width: 220, height: 220, margin: "0 auto", position: "relative" }}>
      <svg viewBox="0 0 200 200" style={{ width: "100%", height: "100%", filter: "drop-shadow(0px 10px 20px rgba(0,0,0,0.5))" }}>
        
        {/* Dark Matte Background Face of the Speedometer */}
        <circle cx={center} cy={center} r="98" fill="#111111" stroke="#222222" strokeWidth="2" />
        <circle cx={center} cy={center} r="96" fill="#0a0a0a" />

        {/* Thin Red Arc (80 to 160 range) */}
        <path 
          d={redArcPath} 
          fill="none" 
          stroke="#e63946" 
          strokeWidth="1" 
          strokeLinecap="round" 
        />

        {/* Ticks & Numbers */}
        {ticks}

        {/* Digital Readout */}
        <text 
          x={center} 
          y="158" 
          fill="#ffffff" 
          fontSize="18" 
          fontWeight="bold" 
          fontFamily="Arial, Helvetica, sans-serif"
          textAnchor="middle" 
        >
          {Math.round(displayValue)}
        </text>
        <text x={center} y="170" fill="#666666" fontSize="8" fontWeight="bold" textAnchor="middle" letterSpacing="1">
          KNOTS
        </text>

        {/* Animated Solid Red Needle */}
        <g transform={`rotate(${angle} ${center} ${center})`}>
          <line 
            x1={center} 
            y1={center} 
            x2={center} 
            y2="22" 
            stroke="#e63946" 
            strokeWidth="3.5" 
            strokeLinecap="round" 
            filter="drop-shadow(0px 2px 4px rgba(0,0,0,0.6))"
          />
        </g>
        
        {/* Central Cap */}
        <circle cx={center} cy={center} r="12" fill="#1a1a1a" />
        <circle cx={center} cy={center} r="10" fill="#111111" stroke="#2a2a2a" strokeWidth="1" />
      </svg>
    </MDBox>
  );
};

/**
 * VehicleDashboard: Transparent container with speedometer in focus and ignition at bottom.
 */
const VehicleDashboard = ({ vehicle }) => {
  const [isOpen, setIsOpen] = useState(true);

  if (!vehicle) return null;

  // Convert m/s to km/h assuming vehicle.speed is passed in m/s
  const speed = Number(vehicle.speed * 1.852) || 0;
  const isIgnitionOn = vehicle.ignition === "ON";



  return (
    <MDBox
      sx={{
        position: "absolute",
        bottom: 30,
        right: 30,
        zIndex: 2000,
        transition: "transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      {isOpen ? (
        <MDBox
          sx={{
            width: 260,
            background: "transparent", // Removed the black box background
            padding: "10px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          {/* Top Close Button */}
          <MDBox width="100%" display="flex" justifyContent="flex-end" mb={1}>
            <div 
              onClick={() => setIsOpen(false)} 
              style={{ 
                cursor: "pointer", 
                color: "#dad0d0", 
                fontSize: "18px", 
                transition: "0.2s",
                background: "rgba(0,0,0,0.5)",
                borderRadius: "50%",
                width: "28px",
                height: "28px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}
              onMouseOver={(e) => e.target.style.color = "#fff"}
              onMouseOut={(e) => e.target.style.color = "#888"}
            >
              ✕
            </div>
          </MDBox>

          {/* Core Speedometer */}
          <RealisticSpeedometer value={speed} max={50} />
          
          {/* Bottom Container: Ignition & Vehicle Name */}
          <MDBox display="flex" flexDirection="column" alignItems="center" mt={2} gap={1}>
            {/* Ignition Badge Moved Here */}
            <MDBox sx={{
              padding: "6px 16px",
              borderRadius: "20px",
              border: `1.5px solid ${isIgnitionOn ? "#00e676" : "#ff3d00"}`,
              display: "flex",
              alignItems: "center",
              gap: "8px",
              background: "rgba(0,0,0,0.7)", // Slight dark background just for the pill to be readable
              backdropFilter: "blur(4px)",
            }}>
              <PowerSettingsNewIcon 
                sx={{ 
                  fontSize: 16, 
                  color: isIgnitionOn ? "#00e676" : "#ff3d00",
                }} 
              />
              <span style={{ fontSize: "11px", fontWeight: "bold", color: isIgnitionOn ? "#00e676" : "#ff3d00", letterSpacing: "0.5px" }}>
                {isIgnitionOn ? "IGNITION ON" : "IGNITION OFF"}
              </span>
            </MDBox>

            {/* Vehicle Name */}
            {/* <span style={{ 
              fontSize: "12px", 
              color: "#fff", 
              fontWeight: "600", 
              letterSpacing: "1px",
              textShadow: "0px 2px 4px rgba(0,0,0,0.8)" // Shadow for readability over maps
            }}>
              {vehicle.name || "ACTIVE VEHICLE"}
            </span> */}
          </MDBox>
        </MDBox>
      ) : (
        /* Minimized State */
        <MDBox
          onClick={() => setIsOpen(true)}
          sx={{
            width: 60,
            height: 60,
            background: "#111",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            border: "2px solid #555",
            boxShadow: "0 5px 15px rgba(0, 0, 0, 0.5)",
            "&:hover": { transform: "scale(1.05)", borderColor: "#888" }
          }}
        >
          <SpeedIcon sx={{ color: "#fff", fontSize: 28 }} />
        </MDBox>
      )}
    </MDBox>
  );
};

export default VehicleDashboard;